import React, { useState, useRef, useEffect } from 'react';

interface DateRangePickerProps {
  dateFrom: string | null;
  dateTo: string | null;
  onChange: (dateFrom: string | null, dateTo: string | null) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateFrom,
  dateTo,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempDateFrom, setTempDateFrom] = useState(dateFrom || '');
  const [tempDateTo, setTempDateTo] = useState(dateTo || '');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sync with prop changes
  useEffect(() => {
    setTempDateFrom(dateFrom || '');
    setTempDateTo(dateTo || '');
  }, [dateFrom, dateTo]);

  const applyDateRange = () => {
    onChange(
      tempDateFrom || null,
      tempDateTo || null
    );
    setIsOpen(false);
  };

  const clearDateRange = () => {
    setTempDateFrom('');
    setTempDateTo('');
    onChange(null, null);
  };

  const setQuickRange = (days: number | null) => {
    if (days === null) {
      setTempDateFrom('');
      setTempDateTo('');
      onChange(null, null);
    } else {
      const today = new Date();
      const pastDate = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);
      const fromDate = pastDate.toISOString().split('T')[0];
      const toDate = today.toISOString().split('T')[0];

      setTempDateFrom(fromDate);
      setTempDateTo(toDate);
      onChange(fromDate, toDate);
    }
    setIsOpen(false);
  };

  const formatDisplayDate = (date: string | null) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const hasDateRange = dateFrom || dateTo;
  const displayText = hasDateRange
    ? `${formatDisplayDate(dateFrom) || '...'} - ${formatDisplayDate(dateTo) || '...'}`
    : 'Select date range';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-[44px] px-3 text-left border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer hover:border-gray-400 flex items-center"
      >
        <div className="flex items-center justify-between gap-2 w-full">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className={hasDateRange ? 'text-gray-900 font-medium' : 'text-gray-500'}>{displayText}</span>
          </div>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
          {/* Date Inputs */}
          <div className="space-y-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={tempDateFrom}
                onChange={(e) => setTempDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={tempDateTo}
                onChange={(e) => setTempDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Quick Filters Footer */}
          <div className="border-t border-gray-200 pt-3">
            <div className="text-xs font-medium text-gray-700 mb-2">Quick Ranges</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setQuickRange(1)}
                className="px-3 py-2 text-xs font-medium bg-gray-50 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 rounded-md transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => setQuickRange(7)}
                className="px-3 py-2 text-xs font-medium bg-gray-50 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 rounded-md transition-colors"
              >
                Last 7 Days
              </button>
              <button
                onClick={() => setQuickRange(30)}
                className="px-3 py-2 text-xs font-medium bg-gray-50 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 rounded-md transition-colors"
              >
                Last 30 Days
              </button>
              <button
                onClick={() => setQuickRange(null)}
                className="px-3 py-2 text-xs font-medium bg-gray-50 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 rounded-md transition-colors"
              >
                All Time
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-200">
            <button
              onClick={clearDateRange}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Clear
            </button>
            <button
              onClick={applyDateRange}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
